'use client';

import { IActiveNavConfig, IBlogArticleListItem } from '@/api/types/blog';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useContext, useImperativeHandle, useMemo, useRef } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper as SwiperComponent, SwiperProps, SwiperSlide } from 'swiper/react';
import ArticleCard from './ArticleCard';
import SwiperNavigationButtons from './SwiperNavigationButtons';
import { useTranslations } from '@/i18n/use-translations';

// 神策参数类型
interface SensorParams {
  mkt_name: string;
  pits_index: number;
  article_name?: string;
  author_name?: string;
  click_area?: string;
  pits_jump_type?: string;
  pits_jump_content?: string;
}

interface TopReadArticlesProps {
  topReadArticles: IBlogArticleListItem[];
  activeNav: IActiveNavConfig;
  sdBlogPageMktExp?: (params: SensorParams) => void;
  sdBlogPageMktClick?: (params: SensorParams) => void;
  className?: string;
  onRef?: (ref: { resetSwiper: () => void }) => void;
}

const TopReadArticles: React.FC<TopReadArticlesProps> = ({
  topReadArticles = [],
  // activeNav,
  sdBlogPageMktExp = () => {},
  sdBlogPageMktClick = () => {},
  className = '',
  onRef,
}: TopReadArticlesProps) => {
  const t = useTranslations('blog.components');
  const { deviceType } = useContext(ResponsiveContext);
  const swiperRef = useRef<any>(null);

  // 暴露重置轮播的方法
  useImperativeHandle(
    onRef,
    () => ({
      resetSwiper: () => {
        if (swiperRef.current) {
          swiperRef.current.slideTo(0, 0); // 滑动到第一个，无动画
        }
      },
    }),
    [],
  );

  // Swiper配置选项

  const swiperOptions = useMemo(() => {
    const options: SwiperProps = {
      modules: [Navigation],
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 'auto' as const,
      navigation: {
        prevEl: '.top-read-prev-btn',
        nextEl: '.top-read-next-btn',
      },
    };
    if (deviceType === EPlatform.WEB) {
      options.spaceBetween = 24;
      options.slidesOffsetAfter = 48;
    } else {
      options.spaceBetween = 12;
      options.slidesOffsetBefore = 14;
      options.slidesOffsetAfter = 14;
    }
    return options;
  }, [deviceType]);

  // 生成神策参数
  const sensorParams = useCallback(
    (article: IBlogArticleListItem, index: number): Omit<SensorParams, 'click_area'> => {
      return {
        mkt_name: '高阅读量推荐',
        pits_index: index,
        article_name: article.name,
        author_name: article.blog_author_name,
        pits_jump_type: '4',
        pits_jump_content: `/blog/${article.another_name || 'all-blogs'}/${article.jump_path}`,
      };
    },
    [],
  );

  // 处理作者点击
  const handleAuthorClick = useCallback(
    (article: IBlogArticleListItem, index: number) => {
      sdBlogPageMktClick({
        ...sensorParams(article, index),
        click_area: 'author',
      });
    },
    [sdBlogPageMktClick, sensorParams],
  );

  // 处理文章点击
  const handleArticleClick = useCallback(
    (article: IBlogArticleListItem, index: number) => {
      sdBlogPageMktClick({
        ...sensorParams(article, index),
        click_area: 'article',
      });
    },
    [sdBlogPageMktClick, sensorParams],
  );

  // 处理文章曝光
  const handleArticleExp = useCallback(
    (article: IBlogArticleListItem, index: number) => {
      sdBlogPageMktExp({
        ...sensorParams(article, index),
      });
    },
    [sdBlogPageMktExp, sensorParams],
  );

  if (!topReadArticles.length) {
    return null;
  }

  return (
    <div
      className={`
        top-read-articles box-border
        ${className}
        ${deviceType === EPlatform.WEB ? 'w-[calc(100%+48px)] mt-[2.5vw]' : 'w-full mt-32'}
      `}
    >
      {/* 标题 */}
      <div
        className={`title ${deviceType === EPlatform.WEB ? 'flex justify-between items-baseline' : 'pl-14'}`}
      >
        <h2 className={`font-bb ${deviceType === EPlatform.WEB ? 'text-32' : 'text-20'}`}>
          {t('topReadArticles')}
        </h2>
        <SwiperNavigationButtons
          prevButtonClass="top-read-prev-btn"
          nextButtonClass="top-read-next-btn"
          prevButtonExtraClass="blog-top-read-articles-nav-left"
          nextButtonExtraClass="blog-top-read-articles-nav-right"
        />
      </div>

      {/* 文章轮播 */}
      <div className={`articles ${deviceType === EPlatform.WEB ? 'mt-[32px]' : 'mt-20'}`}>
        <SwiperComponent
          {...swiperOptions}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          className="top-read-swiper"
        >
          {topReadArticles.map((item, index) => (
            <SwiperSlide
              key={item.id}
              className="swiper-item"
              style={{ width: `${deviceType === EPlatform.WEB ? '356px' : '307px'}` }}
            >
              <ArticleCard
                article={item}
                showIntro={false}
                onArticleClick={() => handleArticleClick(item, index)}
                onAuthorClick={() => handleAuthorClick(item, index)}
                onArticleExp={() => handleArticleExp(item, index)}
                variant="top-read-article"
              />
            </SwiperSlide>
          ))}
        </SwiperComponent>
      </div>
    </div>
  );
};

export default TopReadArticles;
